import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { NotebookPen, Plus, Trash2, ImagePlus, X } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/admin/notes`;
const PUBLIC_API = `${process.env.REACT_APP_BACKEND_URL}/api/notes`;
const GOLD = "#f5a623";
const EMPTY = { title: "", body: "", link_label: "", link_url: "", image_alt: "" };

const box = { background: "#131c33", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 16, padding: 24, marginTop: 24 };
const input = { background: "rgba(0,0,0,0.3)", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 10, color: "#fff", padding: "10px 12px", fontSize: 14, width: "100%" };
const btn = (bg = GOLD, fg = "#1a2744") => ({ background: bg, color: fg, border: "none", borderRadius: 10, padding: "10px 16px", fontWeight: 700, fontSize: 13, textTransform: "uppercase", letterSpacing: "0.06em", cursor: "pointer" });
const ghost = { ...btn("transparent", "#fff"), border: "1px solid rgba(255,255,255,0.2)" };

const NoteEditor = ({ note, headers, onDone, setMsg }) => {
  const [f, setF] = useState(note ? { ...EMPTY, ...Object.fromEntries(Object.entries(note).map(([k, v]) => [k, v ?? ""])) } : EMPTY);
  const [saved, setSaved] = useState(note);
  const [busy, setBusy] = useState(false);
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  const save = async (status) => {
    setBusy(true);
    const body = { title: f.title, body: f.body, status, link_label: f.link_label || null, link_url: f.link_url || null, image_alt: f.image_alt || null };
    try {
      const { data } = saved ? await axios.put(`${API}/${saved.id}`, body, { headers }) : await axios.post(API, body, { headers });
      setSaved(data);
      setMsg(status === "published" ? "Published — it's live on /notes" : "Saved as draft");
      if (status === "published" || saved) onDone();
    } catch (e) {
      setMsg(e.response?.data?.detail?.toString() || "Couldn't save — check the title and text");
    }
    setBusy(false);
  };

  const upload = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !saved) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await axios.post(`${API}/${saved.id}/image`, fd, { headers });
      setSaved(data);
      setMsg("Photo added");
    } catch (err) {
      setMsg(err.response?.data?.detail || "Photo upload failed");
    }
    setBusy(false);
  };

  const removeImage = async () => {
    setBusy(true);
    try {
      const { data } = await axios.delete(`${API}/${saved.id}/image`, { headers });
      setSaved(data);
    } catch {
      setMsg("Couldn't remove the photo");
    }
    setBusy(false);
  };

  return (
    <div style={{ display: "grid", gap: 10, marginTop: 16, padding: 16, borderRadius: 12, background: "rgba(255,255,255,0.03)" }} data-testid="notes-editor">
      <input placeholder="Title" value={f.title} onChange={set("title")} style={input} data-testid="notes-editor-title" />
      <textarea placeholder="Write it like you'd tell a neighbor. Leave a blank line between paragraphs." rows={8} value={f.body} onChange={set("body")} style={{ ...input, resize: "vertical", lineHeight: 1.5 }} data-testid="notes-editor-body" />
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <input placeholder="Optional link text (e.g. See the project)" value={f.link_label} onChange={set("link_label")} style={{ ...input, flex: "1 1 220px", width: "auto" }} data-testid="notes-editor-link-label" />
        <input placeholder="Link (e.g. /projects/ezitemizer)" value={f.link_url} onChange={set("link_url")} style={{ ...input, flex: "1 1 220px", width: "auto" }} data-testid="notes-editor-link-url" />
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap" }}>
        {saved?.has_image && (
          <span style={{ position: "relative" }}>
            <img src={`${PUBLIC_API}/${saved.id}/image?v=${encodeURIComponent(saved.updated_at)}`} alt="" style={{ height: 64, borderRadius: 6 }} data-testid="notes-editor-thumb" />
            <button type="button" onClick={removeImage} title="Remove photo" style={{ position: "absolute", top: -8, right: -8, background: "#ff8a8a", border: "none", borderRadius: 999, padding: 2, cursor: "pointer" }} data-testid="notes-editor-remove-image"><X size={12} /></button>
          </span>
        )}
        <label style={{ ...ghost, display: "inline-flex", gap: 6, alignItems: "center", opacity: saved ? 1 : 0.45, cursor: saved ? "pointer" : "not-allowed" }} title={saved ? "" : "Save the note first"}>
          <ImagePlus size={14} /> {saved?.has_image ? "Replace photo" : "Add photo"}
          <input type="file" accept="image/jpeg,image/png,image/webp" hidden disabled={!saved || busy} onChange={upload} data-testid="notes-editor-image-input" />
        </label>
        <input placeholder="Photo caption (optional)" value={f.image_alt} onChange={set("image_alt")} style={{ ...input, flex: "1 1 200px", width: "auto" }} data-testid="notes-editor-image-alt" />
      </div>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
        <button type="button" disabled={busy} style={btn()} onClick={() => save("published")} data-testid="notes-editor-publish">{saved?.status === "published" ? "Update live note" : "Publish"}</button>
        <button type="button" disabled={busy} style={ghost} onClick={() => save("draft")} data-testid="notes-editor-save-draft">{saved?.status === "published" ? "Unpublish (draft)" : "Save draft"}</button>
        <button type="button" style={{ ...ghost, border: "none" }} onClick={onDone} data-testid="notes-editor-close">Close</button>
      </div>
    </div>
  );
};

export const NotesPanel = ({ password }) => {
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null);
  const [msg, setMsg] = useState(null);
  const headers = { "x-stats-password": password };

  const load = useCallback(() => {
    axios.get(API, { headers: { "x-stats-password": password } }).then(({ data }) => setNotes(data)).catch(() => {});
  }, [password]);
  useEffect(load, [load]);

  const done = () => { setEditing(null); load(); };
  const remove = async (n) => {
    if (!window.confirm(`Delete "${n.title}"?`)) return;
    await axios.delete(`${API}/${n.id}`, { headers });
    setMsg("Note deleted");
    load();
  };

  return (
    <section style={box} data-testid="notes-panel">
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
        <h3 style={{ margin: 0, fontFamily: '"Roboto Condensed", sans-serif', fontSize: 18, color: "#fff", display: "flex", gap: 8, alignItems: "center" }}>
          <NotebookPen size={18} color={GOLD} /> Notes from the Shop
        </h3>
        <div style={{ display: "flex", gap: 8 }}>
          <a href="/notes" target="_blank" rel="noreferrer" style={{ ...ghost, textDecoration: "none" }} data-testid="notes-view-page">View /notes</a>
          <button type="button" style={{ ...btn(), display: "inline-flex", gap: 6, alignItems: "center" }} onClick={() => setEditing("new")} data-testid="notes-new-btn"><Plus size={14} /> New note</button>
        </div>
      </div>
      {msg && <p style={{ color: GOLD, fontSize: 13, marginTop: 10 }} data-testid="notes-msg">{msg}</p>}
      {editing === "new" && <NoteEditor headers={headers} onDone={done} setMsg={setMsg} />}
      <ul style={{ listStyle: "none", padding: 0, margin: "16px 0 0" }} data-testid="notes-admin-list">
        {notes.length === 0 && <li style={{ color: "rgba(255,255,255,0.45)", fontSize: 13 }}>No notes yet.</li>}
        {notes.map((n) => (
          <li key={n.id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)", padding: "10px 0" }} data-testid={`notes-admin-item-${n.id}`}>
            <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", fontSize: 13 }}>
              <span style={{ color: n.status === "published" ? "#4ade80" : GOLD, textTransform: "uppercase", fontSize: 11, letterSpacing: "0.1em", minWidth: 70 }}>{n.status}</span>
              <span style={{ color: "#fff", fontWeight: 700, flex: 1 }}>{n.title}</span>
              {n.has_image && <span style={{ color: "rgba(255,255,255,0.45)", fontSize: 11 }}>photo</span>}
              <button type="button" style={{ ...ghost, padding: "6px 10px" }} onClick={() => setEditing(editing === n.id ? null : n.id)} data-testid={`notes-edit-${n.id}`}>Edit</button>
              <button type="button" title="Delete" style={{ ...ghost, padding: 6 }} onClick={() => remove(n)} data-testid={`notes-delete-${n.id}`}><Trash2 size={14} /></button>
            </div>
            {editing === n.id && <NoteEditor note={n} headers={headers} onDone={done} setMsg={setMsg} />}
          </li>
        ))}
      </ul>
    </section>
  );
};
